
import React from 'react';
// Fix: Use namespace import to bypass missing exported member errors in react-router-dom
import * as ReactRouterDom from 'react-router-dom';
// Fix: Use namespace import to bypass missing exported member errors in @tanstack/react-query
import * as ReactQuery from '@tanstack/react-query';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DashboardLayout } from './layouts/DashboardLayout';
import { Loader2 } from 'lucide-react';

import LoginPage from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import PeopleList from './pages/PeopleList';
import PersonDetail from './pages/PersonDetail';
import ProgramsList from './pages/ProgramsList';
import ProgramDetail from './pages/ProgramDetail';
import EngagementsList from './pages/EngagementsList';
import EngagementDetail from './pages/EngagementDetail';
import SessionsList from './pages/SessionsList';
import SessionDetail from './pages/SessionDetail';
import OrganizationsList from './pages/OrganizationsList';
import OrganizationDetail from './pages/OrganizationDetail';
import AdminSettings from './pages/AdminSettings';
import ImportWizard from './pages/ImportWizard';
import NetworkMap from './pages/NetworkMap';
import ReportsPage from './pages/Reports';

const { BrowserRouter: Router, Routes, Route, Navigate } = ReactRouterDom as any;
const { QueryClient, QueryClientProvider } = ReactQuery as any;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { session, isLoading } = useAuth();

  if (isLoading && !session) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white">
        <Loader2 size={48} className="text-slate-900 animate-spin mb-6" />
        <p className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-300">Establishing Session</p>
      </div>
    );
  }

  if (!session) return <Navigate to="/login" replace />;
  return <>{children}</>;
};

const ManagementRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { profile, isLoading } = useAuth();
  if (isLoading) return null;
  const isManager = profile?.role === 'system_admin' || profile?.role === 'org_manager';
  if (!isManager) return <Navigate to="/" replace />;
  return <>{children}</>;
};

const SysAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { profile, isLoading } = useAuth();
  if (isLoading) return null;
  const isAdmin = profile?.role === 'system_admin';
  if (!isAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            
            <Route path="/" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route index element={<Dashboard />} />
              <Route path="people" element={<PeopleList />} />
              <Route path="people/:id" element={<PersonDetail />} />
              
              <Route path="programs" element={<ProgramsList />} />
              <Route path="programs/:id" element={<ProgramDetail />} />
              <Route path="engagements" element={<EngagementsList />} />
              <Route path="engagements/:id" element={<EngagementDetail />} />
              <Route path="sessions" element={<SessionsList />} />
              <Route path="sessions/:id" element={<SessionDetail />} />
              
              <Route path="organizations" element={<OrganizationsList />} />
              <Route path="organizations/:id" element={<OrganizationDetail />} />
              <Route path="admin" element={<AdminSettings />} />
              <Route path="import" element={<ImportWizard />} />
              <Route path="reports" element={<ReportsPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
